import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IconButton, Box, Typography, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { v4 as uuidv4 } from 'uuid';
import { analyzeTask, TaskSuggestion } from '../services/geminiService';

// Constants for optimization
const MAX_CHUNK_SIZE = 1024 * 1024; // 1MB
const MAX_RECORDING_DURATION = 30 * 60 * 1000; // 30 minutes
const DEBOUNCE_DELAY = 300; // ms
const AUDIO_CONFIG = {
  sampleRate: 44100,
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  latency: 0
};

// Add TypeScript declarations for the Web Speech API
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

// Add TypeScript definitions for the Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  interpretation: any;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onnomatch: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob, text: string, suggestions: TaskSuggestion[]) => void;
  language?: string;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ 
  onRecordingComplete,
  language = 'en-US'
}) => {
  const [recordingState, setRecordingState] = useState({
    isRecording: false,
    transcription: '',
    error: null as string | null,
    duration: 0,
    isProcessing: false,
    confidence: 0,
    suggestions: [] as TaskSuggestion[]
  });
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  // Initialize audio context and analyzer
  const initializeAudioContext = useCallback(async (stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 2048;
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
    } catch (error) {
      console.error('Error initializing audio context:', error);
    }
  }, []);

  // Process audio chunks with better quality
  const processAudioChunk = useCallback((chunk: Blob) => {
    if (audioChunksRef.current.length * MAX_CHUNK_SIZE > MAX_RECORDING_DURATION) {
      audioChunksRef.current.shift();
    }
    audioChunksRef.current.push(chunk);
  }, []);

  // Enhanced cleanup function
  const cleanup = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    audioChunksRef.current = [];
    setRecordingState(prev => ({
      ...prev,
      isRecording: false,
      error: null,
      duration: 0,
      isProcessing: false
    }));
  }, []);

  // Initialize speech recognition with enhanced settings
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setRecordingState(prev => ({
        ...prev,
        error: 'Speech recognition not supported in this browser'
      }));
      return;
    }

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.maxAlternatives = 3;
    recognitionRef.current.lang = language;

    recognitionRef.current.onstart = () => {
      console.log('Speech recognition started');
    };

    recognitionRef.current.onend = () => {
      console.log('Speech recognition ended');
      if (recordingState.isRecording && recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (error) {
          console.error('Error restarting speech recognition:', error);
          setRecordingState(prev => ({
            ...prev,
            error: 'Failed to restart speech recognition'
          }));
        }
      }
    };

    recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';
      let maxConfidence = 0;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence;

        if (result.isFinal) {
          finalTranscript += transcript;
          maxConfidence = Math.max(maxConfidence, confidence);
        } else {
          interimTranscript += transcript;
        }
      }

      setRecordingState(prev => ({
        ...prev,
        transcription: finalTranscript || interimTranscript,
        confidence: maxConfidence
      }));
    };

    recognitionRef.current.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error);
      
      switch (event.error) {
        case 'no-speech':
          if (recordingState.isRecording && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (error) {
              console.error('Error restarting speech recognition after no-speech:', error);
            }
          }
          break;
        case 'audio-capture':
          setRecordingState(prev => ({
            ...prev,
            error: 'Microphone access error. Please check your microphone settings.'
          }));
          break;
        case 'network':
          setRecordingState(prev => ({
            ...prev,
            error: 'Network error occurred. Please check your connection.'
          }));
          break;
        default:
          setRecordingState(prev => ({
            ...prev,
            error: `Speech recognition error: ${event.error}`
          }));
      }
    };

    return cleanup;
  }, [cleanup, language, recordingState.isRecording]);

  const startRecording = async () => {
    try {
      setRecordingState(prev => ({ ...prev, isProcessing: true }));
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONFIG
      });
      
      await initializeAudioContext(stream);
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        transcription: '',
        error: null,
        duration: 0,
        isProcessing: false
      }));

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          processAudioChunk(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setRecordingState(prev => ({ ...prev, isProcessing: true }));
        
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const fileName = `recording_${uuidv4()}.webm`;
          const file = new File([audioBlob], fileName, { type: 'audio/webm' });
          
          await handleRecordingComplete(audioBlob, recordingState.transcription);
        } catch (error) {
          console.error('Error processing recording:', error);
          setRecordingState(prev => ({
            ...prev,
            error: 'Error processing recording'
          }));
        } finally {
          cleanup();
        }
      };

      mediaRecorder.start(1000);
      
      if (recognitionRef.current) {
        recognitionRef.current.start();
      }
      
      timerRef.current = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }));
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      setRecordingState(prev => ({
        ...prev,
        error: 'Error accessing microphone. Please ensure you have granted microphone permissions.',
        isProcessing: false
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState.isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRecordingComplete = async (audioBlob: Blob, transcription: string) => {
    if (transcription.trim()) {
      try {
        setRecordingState(prev => ({ ...prev, isProcessing: true }));
        const suggestion = await analyzeTask(transcription);
        setRecordingState(prev => ({
          ...prev,
          transcription,
          suggestions: [suggestion],
          isProcessing: false
        }));
        onRecordingComplete(audioBlob, transcription, [suggestion]);
      } catch (error) {
        console.error('Error analyzing task:', error);
        setRecordingState(prev => ({
          ...prev,
          error: 'Error analyzing task',
          isProcessing: false
        }));
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <IconButton
        color={recordingState.isRecording ? 'error' : 'primary'}
        onClick={recordingState.isRecording ? stopRecording : startRecording}
        disabled={recordingState.isProcessing}
        sx={{ 
          width: 48, 
          height: 48,
          backgroundColor: recordingState.isRecording ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.04)',
          '&:hover': {
            backgroundColor: recordingState.isRecording ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.08)',
          }
        }}
      >
        {recordingState.isProcessing ? (
          <CircularProgress size={24} />
        ) : recordingState.isRecording ? (
          <StopIcon />
        ) : (
          <MicIcon />
        )}
      </IconButton>
      {recordingState.isRecording && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Recording: {formatTime(recordingState.duration)}
          </Typography>
          {recordingState.transcription && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Transcription: {recordingState.transcription}
              {recordingState.confidence > 0 && (
                <span style={{ opacity: 0.7 }}>
                  {' '}({Math.round(recordingState.confidence * 100)}% confidence)
                </span>
              )}
            </Typography>
          )}
          {recordingState.suggestions.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Suggested Quadrant: {recordingState.suggestions[0].quadrant}
            </Typography>
          )}
          {recordingState.error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Error: {recordingState.error}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VoiceRecorder; 